// src/contexts/TeacherAuthContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react'
import { onAuthStateChanged } from 'firebase/auth'
import type { User } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { auth, db } from '../lib/firebase-config'

export interface TeacherProfile {
  teacherId: string
  email: string
  displayName: string
  photoURL?: string | null
  authProvider: 'email' | 'google'
  userType: 'teacher'
  teacherInfo?: {
    school?: string
    gradesTaught: string[]
    subjects: string[]
  }
  createdAt: Date
  subscription?: {
    tier: 'free' | 'monthly' | 'annual'
    status: 'active' | 'cancelled'
  }
}

interface TeacherAuthContextType {
  user: User | null
  profile: TeacherProfile | null
  loading: boolean
  signOut: () => Promise<void>
  refetchProfile: () => Promise<void>
}

const TeacherAuthContext = createContext<TeacherAuthContextType>({
  user: null,
  profile: null,
  loading: true,
  signOut: async () => {}
})

export const useTeacherAuth = () => {
  const context = useContext(TeacherAuthContext)
  if (!context) {
    throw new Error('useTeacherAuth must be used within TeacherAuthProvider')
  }
  return context
}

export const TeacherAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<TeacherProfile | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user)
      
      if (user) {
        try {
          const teacherRef = doc(db, 'teachers', user.uid)
          const { getDoc, setDoc } = await import('firebase/firestore')
          const docSnap = await getDoc(teacherRef)
          
          if (docSnap.exists()) {
            // Update lastLoginAt for existing users
            await setDoc(teacherRef, { lastLoginAt: new Date() }, { merge: true })
            setProfile(docSnap.data() as TeacherProfile)
          } else {
            // Profile doesn't exist - will be created by login flow
            setProfile(null)
          }
        } catch (error) {
          console.error('Failed to check teacher profile:', error)
          setProfile(null)
        }
      } else {
        setProfile(null)
      }
      
      setLoading(false)
    })

    return unsubscribe
  }, [])

  const signOut = async () => {
    try {
      await auth.signOut()
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }
  
  const refetchProfile = async () => {
    if (user) {
      try {
        const profileDoc = await getDoc(doc(db, 'teachers', user.uid))
        if (profileDoc.exists()) {
          setProfile(profileDoc.data() as TeacherProfile)
        }
      } catch (error) {
        console.error('Error refetching profile:', error)
      }
    }
  }
  
  return (
    <TeacherAuthContext.Provider value={{ user, profile, loading, signOut, refetchProfile }}>
      {children}
    </TeacherAuthContext.Provider>
  )
}